import { RevoluteJoint, World, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { box, ball } from "./helpers";

/**
 * A plank bridge stitched together with {@link RevoluteJoint}s and pinned to two
 * fixed posts. Jointed planks don't collide with each other (`collideConnected`
 * defaults off), so the deck flexes cleanly instead of fighting itself.
 *
 * The deck is a 14-link constraint chain, so it needs more solver iterations
 * than the default to stay taut under load — otherwise a Gauss–Seidel solver
 * can't propagate the constraint along the whole chain in one step and the deck
 * sinks. Dropped weights are also kept light and capped in number so the load
 * can never overwhelm the chain (and so nothing builds enough speed to tunnel
 * through the thin planks).
 */
export const bridge: Demo = {
	id: "bridge",
	title: "Rope bridge",
	blurb:
		"A plank deck stitched from revolute joints and pinned to two posts. Click to drop weights and watch it sag, sway, and spring back.",
	category: "Joints & Machines",
	build(): Scene {
		const world = new World({
			gravity: [0, 9.81],
			// A 14-link pinned revolute chain needs more temporal substeps to stay taut
			// and stable. In the TGS-Soft solver, raising `substeps` (smaller h, fresher
			// joint anchors, one joint position-correction pass per substep) is the right
			// lever — far steadier than cranking `substepIterations`, which over-stiffens
			// the chain and churns the soft-contact relax/friction passes every substep.
			// The legacy velocity/positionIterations knobs are ignored under TGS (substeps>0).
			solver: { substepIterations: 2 },
			sleep: {
				timeToSleep: 0.35,
				linearTolerance: 0.12,
				angularTolerance: 0.12,
				resetFactor: 2.5,
				leakRate: 0.25,
			},
		});

		const count = 14;
		const plankW = 0.7;
		const plankH = 0.3;
		const gap = 0.05;
		const step = plankW + gap;
		const span = (count - 1) * step;
		const startX = -span / 2;
		const y = -2;

		// Decorative static posts at each end (the deck pins to fixed world points).
		const leftAnchorX = startX - plankW / 2;
		const rightAnchorX = startX + span + plankW / 2;
		box(world, leftAnchorX - 0.4, y + 1.2, 0.8, 3.2, { isStatic: true });
		box(world, rightAnchorX + 0.4, y + 1.2, 0.8, 3.2, { isStatic: true });
		const hiddenBodies = new Set<Body>();
		hiddenBodies.add(box(world, 0, 5.5, 26, 1, { isStatic: true, friction: 0.8 }));
		hiddenBodies.add(box(world, -12.8, -1.2, 1, 13.5, { isStatic: true, friction: 0.8 }));
		hiddenBodies.add(box(world, 12.8, -1.2, 1, 13.5, { isStatic: true, friction: 0.8 }));

		const planks: Body[] = [];
		for (let i = 0; i < count; i++) {
			planks.push(box(world, startX + i * step, y, plankW, plankH, { density: 1.6, friction: 0.9 }));
		}

		// Pin the two ends to fixed world points...
		world.addConstraint(
			new RevoluteJoint(planks[0]!, null, {
				localAnchorA: [-plankW / 2, 0],
				localAnchorB: [leftAnchorX, y],
			}),
		);
		world.addConstraint(
			new RevoluteJoint(planks[count - 1]!, null, {
				localAnchorA: [plankW / 2, 0],
				localAnchorB: [rightAnchorX, y],
			}),
		);
		// ...and hinge each plank to the next at their shared edge.
		for (let i = 0; i < count - 1; i++) {
			world.addConstraint(
				new RevoluteJoint(planks[i]!, planks[i + 1]!, {
					localAnchorA: [step / 2, 0],
					localAnchorB: [-step / 2, 0],
				}),
			);
		}

		// A couple of weights pre-loaded to make the bridge bow on entry.
		ball(world, -1.5, -2.75, 0.5, { density: 2.2, friction: 0.6 });
		ball(world, 1.8, -2.8, 0.55, { density: 2.2, friction: 0.6 });

		// Cap the number of player-dropped weights so the deck can never be
		// overwhelmed into sinking through itself; recycle the oldest.
		const dropped: Body[] = [];
		const maxDropped = 8;

		return {
			world,
			camera: new Camera(0, -1.4, 34),
			hiddenBodies,
			hint: "Click to drop a weight",
			// The bridge is about dropping weights and watching the deck sway and
			// spring back — not grab-and-throw. Grab-dragging a heavy body with the
			// mouse joint's force can overpower the flexible revolute-joint chain and
			// squeeze it through the flexing deck (no thin wall is skipped, so this is
			// a soft-constraint/force-limit issue, not something CCD would fix). Rather
			// than tune magic force constants, we simply scope this demo to its actual
			// interaction. Grab-dragging stays fully responsive on every other demo.
			draggable: false,
			onPointerDown(x, yWorld): void {
				const b = ball(world, x, yWorld, 0.36 + Math.random() * 0.22, {
					density: 3,
					friction: 0.6,
				});
				dropped.push(b);
				if (dropped.length > maxDropped) world.remove(dropped.shift()!);
			},
		};
	},
};
